import React, { useState, useEffect, ChangeEvent } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Typography,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Snackbar,
    Alert
} from '@mui/material';
import {
    PersonAdd as PersonAddIcon,
    PersonRemove as PersonRemoveIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    Person as PersonIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Timeline as TimelineIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { projectService, AssignableUser } from '../../services/projectService';
import { format } from 'date-fns';
import { jobVacancyService } from '../../services/jobVacancyService';
import { formatCurrency } from '../../utils/format';
import { ProjectPosition, PositionStatus, PositionCost } from '../../types/project';
import { CreateVacancyData, VacancyStatus } from '../../types/jobVacancy';
import VacancyDetails from './VacancyDetails';

interface PositionWithCosts extends ProjectPosition {
    actual_costs: PositionCost[];
    total_actual_cost: number;
    variance: number;
    variance_percentage: number;
    job_vacancy_id?: number;
}

interface ProjectPositionsProps {
    projectId: number;
    positions: ProjectPosition[];
    onOpenVacancy: (positionId: number) => void;
}

const statusColors = {
    PLANNED: 'default',
    OPEN: 'primary',
    IN_PROGRESS: 'warning',
    FILLED: 'success',
    CANCELLED: 'error'
};

const statusLabels = {
    PLANNED: 'Planejada',
    OPEN: 'Vaga Aberta',
    IN_PROGRESS: 'Em Processo',
    FILLED: 'Preenchida',
    CANCELLED: 'Cancelada'
};

const ProjectPositions: React.FC<ProjectPositionsProps> = ({ projectId, positions, onOpenVacancy }) => {
    const { t } = useTranslation();
    const [selectedPosition, setSelectedPosition] = useState<PositionWithCosts | null>(null);
    const [showCostDialog, setShowCostDialog] = useState(false);
    const [showVacancyDialog, setShowVacancyDialog] = useState(false);
    const [showVacancyDetails, setShowVacancyDetails] = useState(false);
    const [newCost, setNewCost] = useState({ month: '', cost: '' });
    const [positionsWithCosts, setPositionsWithCosts] = useState<PositionWithCosts[]>([]);
    const [showPositionDetails, setShowPositionDetails] = useState(false);
    const [loading, setLoading] = useState(false);

    // Campos para criação de vaga
    const [description, setDescription] = useState('');
    const [requirements, setRequirements] = useState('');

    // Alocação de colaborador na posição
    const [users, setUsers] = useState<AssignableUser[]>([]);
    const [staffPosition, setStaffPosition] = useState<PositionWithCosts | null>(null);
    const [staffForm, setStaffForm] = useState({ user_id: '', start_date: '', end_date: '', allocation_percentage: '100' });
    const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

    useEffect(() => {
        loadPositionsCosts();
    }, [positions]);

    useEffect(() => {
        projectService.getAssignableUsers(projectId).then(setUsers).catch(() => setUsers([]));
    }, [projectId]);

    const userName = (userId?: number | null) => users.find((u) => u.id === userId)?.name;

    const openStaffDialog = (position: PositionWithCosts) => {
        setStaffPosition(position);
        setStaffForm({ user_id: '', start_date: format(new Date(), 'yyyy-MM-dd'), end_date: '', allocation_percentage: '100' });
    };

    const handleFillPosition = async () => {
        if (!staffPosition?.id) return;
        try {
            setLoading(true);
            await projectService.fillPosition(projectId, staffPosition.id, {
                user_id: Number(staffForm.user_id),
                start_date: staffForm.start_date,
                end_date: staffForm.end_date || null,
                allocation_percentage: Number(staffForm.allocation_percentage),
            });
            setFeedback({ message: `${userName(Number(staffForm.user_id)) || 'Colaborador'} alocado(a) em ${staffPosition.role}`, severity: 'success' });
            setStaffPosition(null);
            onOpenVacancy(staffPosition.id);
        } catch (error: any) {
            setFeedback({ message: error?.response?.data?.error || 'Erro ao alocar colaborador', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleReleasePosition = async (position: PositionWithCosts) => {
        if (!position.id || !window.confirm(`Liberar a posição ${position.role}? A alocação do colaborador será removida.`)) return;
        try {
            await projectService.releasePosition(projectId, position.id);
            setFeedback({ message: 'Posição liberada', severity: 'success' });
            onOpenVacancy(position.id);
        } catch (error: any) {
            setFeedback({ message: error?.response?.data?.error || 'Erro ao liberar posição', severity: 'error' });
        }
    };

    const staffDatesInvalid = !!staffForm.end_date && staffForm.end_date < staffForm.start_date;

    const loadPositionsCosts = async () => {
        try {
            const costs = await projectService.getProjectCosts(projectId);
            
            const positionsWithCosts = await Promise.all(positions.map(async (position: ProjectPosition) => {
                const positionCosts = costs.positions.find((p: { id: number }) => p.id === position.id);
                let vacancy = undefined;
                
                if (position.id !== undefined) {
                    const vacancies = await jobVacancyService.getProjectVacancies(projectId, position.id);
                    vacancy = vacancies[0]; // Pega a primeira vaga, se existir
                }
                
                return {
                    ...position,
                    actual_costs: positionCosts?.actual_costs || [],
                    total_actual_cost: positionCosts?.total_actual_cost || 0,
                    variance: positionCosts?.variance || 0,
                    variance_percentage: positionCosts?.variance_percentage || 0,
                    job_vacancy_id: vacancy?.id
                };
            }));
            
            setPositionsWithCosts(positionsWithCosts);
        } catch (error) {
            console.error('Error loading costs:', error);
        }
    };

    const handleOpenCostDialog = (position: PositionWithCosts) => {
        setSelectedPosition(position);
        setShowCostDialog(true);
    };

    const handleCloseCostDialog = () => {
        setSelectedPosition(null);
        setShowCostDialog(false);
        setNewCost({ month: '', cost: '' });
    };

    const handleOpenVacancyDialog = (position: PositionWithCosts) => {
        setSelectedPosition(position);
        setShowVacancyDialog(true);
    };

    const handleCloseVacancyDialog = () => {
        setSelectedPosition(null);
        setShowVacancyDialog(false);
        setDescription('');
        setRequirements('');
    };

    const handleCreateVacancy = async () => {
        if (!selectedPosition) return;

        setLoading(true);
        try {
            const data: CreateVacancyData = {
                project_position_id: selectedPosition.id!,
                contract_type: selectedPosition.contract_type,
                base_salary: selectedPosition.base_salary,
                description,
                requirements,
                is_public: false,
                status: VacancyStatus.DRAFT
            };

            const vacancy = await jobVacancyService.createVacancy(data);
            
            // Atualiza o estado local imediatamente
            setPositionsWithCosts((prevPositions: PositionWithCosts[]) => 
                prevPositions.map((pos: PositionWithCosts) => {
                    if (pos.id === selectedPosition.id) {
                        return {
                            ...pos,
                            status: PositionStatus.OPEN,
                            job_vacancy_id: vacancy.id
                        };
                    }
                    return pos;
                })
            );

            if (selectedPosition.id) {
                await onOpenVacancy(selectedPosition.id);
            }
            handleCloseVacancyDialog();
            loadPositionsCosts();
        } catch (error) {
            console.error('Error creating vacancy:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveCost = async () => {
        if (!selectedPosition) return;

        try {
            await projectService.registerPositionCost(
                projectId,
                selectedPosition.id!,
                newCost.month,
                parseFloat(newCost.cost)
            );
            handleCloseCostDialog();
            loadPositionsCosts();
        } catch (error) {
            console.error('Error saving cost:', error);
        }
    };

    const renderCostVariance = (variance: number, percentage: number) => {
        const color = variance <= 0 ? 'success.main' : 'error.main';
        return (
            <Box display="flex" alignItems="center" color={color}>
                <Typography variant="body2">
                    {formatCurrency(Math.abs(variance))}
                    {' '}
                    ({percentage >= 0 ? '+' : ''}{percentage.toFixed(1)}%)
                </Typography>
                {Math.abs(percentage) > 5 && (
                    <Tooltip title={variance <= 0 ? 'Abaixo do orçamento' : 'Acima do orçamento'}>
                        <WarningIcon fontSize="small" sx={{ ml: 1 }} />
                    </Tooltip>
                )}
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Posições do Projeto
            </Typography>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Cargo</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Tipo de Contrato</TableCell>
                            <TableCell align="right">Salário Base</TableCell>
                            <TableCell align="right">Custo Total</TableCell>
                            <TableCell align="right">Variação</TableCell>
                            <TableCell align="center">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {positionsWithCosts.map((position: PositionWithCosts) => (
                            <TableRow key={position.id}>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography>{position.role}</Typography>
                                        {position.filled_by_user_id && (
                                            <Tooltip title="Posição Preenchida">
                                                <CheckCircleIcon color="success" fontSize="small" />
                                            </Tooltip>
                                        )}
                                    </Box>
                                    {position.filled_by_user_id && (
                                        <Typography variant="caption" color="text.secondary">
                                            {userName(position.filled_by_user_id) || `Usuário #${position.filled_by_user_id}`}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={statusLabels[position.status as keyof typeof statusLabels]}
                                        color={statusColors[position.status as keyof typeof statusColors] as any}
                                        size="small"
                                        icon={position.status === 'OPEN' ? <PersonIcon /> : undefined}
                                    />
                                </TableCell>
                                <TableCell>{position.contract_type}</TableCell>
                                <TableCell align="right">{formatCurrency(position.base_salary)}</TableCell>
                                <TableCell align="right">{formatCurrency(position.total_actual_cost || 0)}</TableCell>
                                <TableCell align="right">
                                    {position.variance !== undefined && position.variance_percentage !== undefined && (
                                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                                            <Typography
                                                color={position.variance < 0 ? 'success.main' : 'error.main'}
                                                sx={{ display: 'flex', alignItems: 'center' }}
                                            >
                                                {position.variance < 0 ? '-' : '+'}
                                                {formatCurrency(Math.abs(position.variance))}
                                                ({Math.abs(position.variance_percentage).toFixed(1)}%)
                                            </Typography>
                                        </Box>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <Box display="flex" justifyContent="center" gap={1}>
                                    {position.status === 'PLANNED' && (
                                        <Tooltip title="Abrir Vaga">
                                            <IconButton
                                                size="small"
                                                    onClick={() => handleOpenVacancyDialog(position)}
                                                    color="primary"
                                            >
                                                <PersonIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                        {position.status === 'OPEN' && position.job_vacancy_id && (
                                            <Tooltip title="Ver Detalhes da Vaga">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedPosition(position);
                                                        setShowVacancyDetails(true);
                                                    }}
                                                    color="primary"
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    {!position.filled_by_user_id && position.status !== 'CANCELLED' && (
                                        <Tooltip title="Alocar colaborador">
                                            <IconButton size="small" color="success" onClick={() => openStaffDialog(position)}>
                                                <PersonAddIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {position.filled_by_user_id && (
                                        <Tooltip title="Liberar posição">
                                            <IconButton size="small" onClick={() => handleReleasePosition(position)}>
                                                <PersonRemoveIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {position.status === 'FILLED' && (
                                        <Tooltip title="Registrar Custo">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenCostDialog(position)}
                                                    color="info"
                                            >
                                                <TimelineIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Ver Detalhes">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setSelectedPosition(position);
                                                    setShowPositionDetails(true);
                                                }}
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Diálogo de Detalhes da Posição */}
            <Dialog
                open={showPositionDetails}
                onClose={() => setShowPositionDetails(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Detalhes da Posição
                </DialogTitle>
                <DialogContent>
                    {selectedPosition && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Cargo
                                </Typography>
                                <Typography variant="body1">
                                    {selectedPosition.role}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Status
                                </Typography>
                                <Chip
                                    label={statusLabels[selectedPosition.status as keyof typeof statusLabels]}
                                    color={statusColors[selectedPosition.status as keyof typeof statusColors] as any}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Tipo de Contrato
                                </Typography>
                                <Typography variant="body1">
                                    {selectedPosition.contract_type}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Salário Base
                                </Typography>
                                <Typography variant="body1">
                                    {formatCurrency(selectedPosition.base_salary)}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Custos
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="subtitle2" color="textSecondary">
                                                        Custo Total
                                                    </Typography>
                                                    <Typography variant="h6">
                                                        {formatCurrency(selectedPosition.total_actual_cost || 0)}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="subtitle2" color="textSecondary">
                                                        Variação
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        color={selectedPosition.variance < 0 ? 'success.main' : 'error.main'}
                                                    >
                                                        {selectedPosition.variance < 0 ? '-' : '+'}
                                                        {formatCurrency(Math.abs(selectedPosition.variance || 0))}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="subtitle2" color="textSecondary">
                                                        Variação %
                                                    </Typography>
                                                    <Typography
                                                        variant="h6"
                                                        color={selectedPosition.variance_percentage < 0 ? 'success.main' : 'error.main'}
                                                    >
                                                        {selectedPosition.variance_percentage < 0 ? '-' : '+'}
                                                        {Math.abs(selectedPosition.variance_percentage || 0).toFixed(1)}%
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowPositionDetails(false)}>
                        Fechar
                    </Button>
                    {selectedPosition?.status === 'PLANNED' && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                handleOpenVacancyDialog(selectedPosition);
                                setShowPositionDetails(false);
                            }}
                        >
                            Abrir Vaga
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Diálogo de Registro de Custo */}
            <Dialog open={showCostDialog} onClose={handleCloseCostDialog}>
                <DialogTitle>
                    Registrar Custo Realizado
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                Cargo: {selectedPosition?.role}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Mês"
                                type="month"
                                value={newCost.month}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setNewCost({ ...newCost, month: e.target.value })}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Custo"
                                type="number"
                                value={newCost.cost}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => setNewCost({ ...newCost, cost: e.target.value })}
                                fullWidth
                                InputProps={{
                                    startAdornment: 'R$'
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCostDialog}>Cancelar</Button>
                    <Button
                        onClick={handleSaveCost}
                        variant="contained"
                        disabled={!newCost.month || !newCost.cost}
                    >
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de Criação de Vaga */}
            <Dialog open={showVacancyDialog} onClose={handleCloseVacancyDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    Abrir Nova Vaga
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                                Cargo: {selectedPosition?.role}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Tipo de Contrato: {selectedPosition?.contract_type}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                Salário Base: {selectedPosition && formatCurrency(selectedPosition.base_salary)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Descrição da Vaga"
                                multiline
                                rows={4}
                                value={description}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
                                margin="normal"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Requisitos"
                                multiline
                                rows={4}
                                value={requirements}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRequirements(e.target.value)}
                                margin="normal"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseVacancyDialog}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateVacancy}
                        disabled={loading}
                    >
                        Criar Vaga
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alocação de colaborador */}
            <Dialog open={!!staffPosition} onClose={() => setStaffPosition(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Alocar colaborador — {staffPosition?.role}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                            <TextField select fullWidth label="Colaborador" value={staffForm.user_id}
                                onChange={(e) => setStaffForm({ ...staffForm, user_id: e.target.value })}
                                helperText="A pessoa passa a ver o projeto na tela de horas">
                                {users.map((u) => (
                                    <MenuItem key={u.id} value={String(u.id)}>{u.name} · {u.role}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="date" label="Início" InputLabelProps={{ shrink: true }}
                                value={staffForm.start_date} onChange={(e) => setStaffForm({ ...staffForm, start_date: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="date" label="Término (opcional)" InputLabelProps={{ shrink: true }}
                                value={staffForm.end_date} onChange={(e) => setStaffForm({ ...staffForm, end_date: e.target.value })}
                                error={staffDatesInvalid} helperText={staffDatesInvalid ? 'Término antes do início' : ''} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="number" label="% de alocação" inputProps={{ min: 1, max: 100 }}
                                value={staffForm.allocation_percentage}
                                onChange={(e) => setStaffForm({ ...staffForm, allocation_percentage: e.target.value })} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStaffPosition(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleFillPosition}
                        disabled={loading || !staffForm.user_id || !staffForm.start_date || staffDatesInvalid}>
                        Alocar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!feedback} autoHideDuration={5000} onClose={() => setFeedback(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {feedback ? <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>{feedback.message}</Alert> : undefined}
            </Snackbar>

            {/* Detalhes da Vaga */}
            {selectedPosition?.job_vacancy_id && (
                <VacancyDetails
                    vacancyId={selectedPosition.job_vacancy_id}
                    open={showVacancyDetails}
                    onClose={() => setShowVacancyDetails(false)}
                    onStatusChange={loadPositionsCosts}
                />
            )}
        </Box>
    );
};

export default ProjectPositions; 